import { useMemo } from 'react';

import { DeviceConfiguration, ForceUpgradeConfiguration, ForceUpgradeType } from './types';
import { useDeviceConfigurationCache } from './useDeviceConfigurationCache';
import { isForceUpgradeEnabled } from './utils';

type UseForceUpgradeConfigurationProps = {
  brandId: number;
};

export function useForceUpgradeConfiguration({ brandId }: UseForceUpgradeConfigurationProps) {
  const { configuration } = useDeviceConfigurationCache();

  const isEnabled = configuration ? isForceUpgradeEnabled({ configuration, brandId }) : false;

  const forceUpgradeConfiguration = useMemo(() => {
    if (configuration && isEnabled) {
      return getForceUpgradeConfiguration(configuration, brandId);
    }
  }, [configuration, isEnabled]);

  return forceUpgradeConfiguration;
}

function getForceUpgradeConfiguration(
  configuration: NonNullable<DeviceConfiguration>,
  brandId: number,
): ForceUpgradeConfiguration | undefined {
  const { forceUpgrade, forceUpgradeLinks } = configuration;

  if (!forceUpgrade) {
    return;
  }

  if (forceUpgrade.brands.blocked.has(brandId)) {
    return {
      type: ForceUpgradeType.Blocked,
    };
  }

  if (forceUpgrade.brands.forced.has(brandId)) {
    return {
      type: ForceUpgradeType.Forced,
      links: forceUpgradeLinks,
    };
  }

  if (forceUpgrade.brands.skippable.has(brandId)) {
    return {
      type: ForceUpgradeType.Skippable,
      links: forceUpgradeLinks,
      skippableInterval: forceUpgrade.skippableInterval,
    };
  }
}
